import { useLingui } from '@lingui/react/macro'
import { useRuntimeVariables } from 'app-kit'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useGeckoAuth } from 'features/auth'
import { createChatApi } from 'features/chatbot/api/chatApi'
import {
  useChatbotSession,
  useChatbotV2,
} from 'features/chatbot/contexts/ChatbotSessionContext'
import {
  ChatHistoryItem,
  useChatHistory,
} from 'features/chatbot/hooks/useChatHistory'
import { ConversationMessage } from 'features/chatbot/types/conversation'
import { mapBackendMessagesToConversation } from 'features/chatbot/utils/mapBackendMessages'
import { Loader2 } from 'lucide-react'
import React, { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { UnixTime, unixTimeNow } from 'types'

dayjs.extend(relativeTime)

const DRAWER_BG = '#1a1a1a'
const BORDER_COLOR = 'rgba(255, 255, 255, 0.08)'
const ITEM_HOVER = 'rgba(255, 255, 255, 0.06)'
const ITEM_ACTIVE = 'rgba(255, 255, 255, 0.1)'
const SECONDARY_TEXT = 'rgba(255, 255, 255, 0.5)'

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1050;
`

const DrawerContainer = styled.div`
  position: absolute;
  top: calc(100% + 0.25rem);
  right: 0;
  width: 18rem;
  max-height: 32rem;
  background: ${DRAWER_BG};
  border: 1px solid ${BORDER_COLOR};
  border-radius: 0.5rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 1051;
`

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.25rem;

  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 2px;
  }
`

const OPEN_INDICATOR = 'rgba(59, 130, 246, 0.35)'

const HistoryItem = styled.button<{ $isOpen?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.25rem 0.5rem;
  background: ${({ $isOpen }) =>
    $isOpen ? 'rgba(59, 130, 246, 0.08)' : 'transparent'};
  border: none;
  border-left: 2px solid
    ${({ $isOpen }) => ($isOpen ? OPEN_INDICATOR : 'transparent')};
  border-radius: 0.25rem;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  text-align: left;
  gap: 0.5rem;
  transition: background 0.1s ease;

  &:hover {
    background: ${({ $isOpen }) =>
      $isOpen ? 'rgba(59, 130, 246, 0.12)' : ITEM_HOVER};
  }

  &:active {
    background: ${ITEM_ACTIVE};
  }
`

const ItemName = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 0.75rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ItemTimestamp = styled.span`
  flex-shrink: 0;
  font-size: 0.625rem;
  color: ${SECONDARY_TEXT};
`

const EmptyState = styled.div`
  padding: 1.5rem;
  text-align: center;
  color: ${SECONDARY_TEXT};
  font-size: 0.8125rem;
`

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  color: ${SECONDARY_TEXT};

  svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`

export const ChatHistoryDrawer: React.FC<ChatHistoryDrawerProps> = ({
  onClose,
}) => {
  const { t } = useLingui()
  const useV2 = useChatbotV2()
  const { sessions, isLoading } = useChatHistory()
  const { loadSession, hydrateSession, switchSession, openSessions } =
    useChatbotSession()
  const containerRef = useRef<HTMLDivElement>(null)
  const { authorization } = useGeckoAuth()
  const { LLM_SERVICE } = useRuntimeVariables()

  const api = useMemo(
    () => createChatApi(LLM_SERVICE, authorization ?? ''),
    [LLM_SERVICE, authorization]
  )

  const openEntryIdToTabId = useMemo(() => {
    const m = new Map<string, string>()
    for (const s of openSessions) {
      if (s.entryId) m.set(s.entryId, s.id)
    }
    return m
  }, [openSessions])

  const merged = useMemo(
    () => mergeOpenSessions(sessions, openSessions),
    [sessions, openSessions]
  )

  const handleSelectSession = async (item: ChatHistoryItem) => {
    const tabId = openEntryIdToTabId.get(item.entryId)
    const openSession = tabId
      ? openSessions.find((session) => session.id === tabId)
      : undefined

    if (tabId && !useV2) {
      switchSession(tabId)
      onClose()
      return
    }
    onClose()

    if (!useV2) {
      loadSession(
        item.entryId,
        item.sessionId ?? item.entryId,
        item.chatLog ?? [],
        item.name
      )
      return
    }

    try {
      const { messages } = await api.loadMessages({ sessionId: item.entryId })
      const conversation = mapBackendMessagesToConversation(messages)
      const lastMessageStatus =
        item.lastMessageStatus ?? openSession?.stream.lastMessageStatus ?? null
      hydrateSession(item.entryId, {
        name: item.name,
        conversation,
        activeStreamId:
          lastMessageStatus === 'in_progress'
            ? (openSession?.stream.activeStreamId ??
              item.activeStreamId ??
              null)
            : null,
        activeMessageId:
          lastMessageStatus === 'in_progress'
            ? (openSession?.stream.activeMessageId ?? null)
            : null,
        lastSeq: Math.max(
          openSession?.stream.lastSeq ?? 0,
          item.persistedThroughSeq ?? 0
        ),
        lastMessageStatus,
      })
    } catch (err) {
      console.warn('[ChatHistoryDrawer] loadMessages failed', err)
    }
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  return (
    <>
      <Overlay onClick={onClose} />
      <DrawerContainer ref={containerRef} onClick={(e) => e.stopPropagation()}>
        <DrawerBody>
          {isLoading ? (
            <LoadingState>
              <Loader2 size={18} />
            </LoadingState>
          ) : merged.length === 0 ? (
            <EmptyState>{t`No previous conversations`}</EmptyState>
          ) : (
            merged.map((item) => {
              const isOpen = openEntryIdToTabId.has(item.entryId)
              return (
                <HistoryItem
                  key={item.entryId}
                  $isOpen={isOpen}
                  onClick={() => handleSelectSession(item)}>
                  <ItemName>{item.name ?? item.preview ?? t`Session`}</ItemName>
                  <ItemTimestamp>
                    {formatTimestamp(item.updatedAt)}
                  </ItemTimestamp>
                </HistoryItem>
              )
            })
          )}
        </DrawerBody>
      </DrawerContainer>
    </>
  )
}

function formatTimestamp(ts: UnixTime): string {
  return dayjs(ts).fromNow()
}

interface ChatHistoryDrawerProps {
  onClose: () => void
}

function mergeOpenSessions(
  persisted: ChatHistoryItem[],
  openSessions: {
    entryId: string | null
    sessionId: string
    name: string | undefined
    conversation: ConversationMessage[]
  }[]
): ChatHistoryItem[] {
  const persistedIds = new Set(persisted.map((s) => s.entryId))
  const extras: ChatHistoryItem[] = []

  for (const session of openSessions) {
    if (!session.entryId) continue
    if (persistedIds.has(session.entryId)) continue
    const visibleMessages = session.conversation.filter(
      (m) => m.type === 'userMessage' || m.type === 'aiResponse'
    )
    if (visibleMessages.length === 0) continue
    const firstUserMsg = session.conversation.find(
      (m) => m.type === 'userMessage'
    )
    const previewText =
      firstUserMsg?.type === 'userMessage' ? firstUserMsg.text : undefined
    extras.push({
      entryId: session.entryId,
      sessionId: session.sessionId,
      name: session.name,
      updatedAt: unixTimeNow(),
      messageCount: visibleMessages.length,
      preview: previewText
        ? previewText.length > 60
          ? `${previewText.slice(0, 60)}...`
          : previewText
        : undefined,
    })
  }

  const merged = [...extras, ...persisted]
  merged.sort((a, b) => (b.updatedAt as number) - (a.updatedAt as number))
  return merged
}
